import React, {useState} from "react";
import {LlmConfig} from "@/model/llmSpecification";
import {Label} from "@/components/ui/label";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table";
import {useIntl} from "react-intl";
import {useDialog} from "@/hook/useDialog";
import toast from "react-hot-toast";
import {useUpdateLlmProviderConfig} from "@/query/llmConfig/useUpdateLlmProviderConfig";
import {Input} from "@/components/ui/input";

type LlmProviderSettingFormProps = {
    llmConfig: LlmConfig;
}

const LlmProviderSettingForm: React.FC<LlmProviderSettingFormProps> = (props) => {
    const intl = useIntl();
    const {llmConfig} = props;
    const dialog = useDialog();
    const [models, setModels] = useState<string[]>(llmConfig.models);

    const {mutateAsync: updateLlmProviderConfig} = useUpdateLlmProviderConfig(llmConfig.provider);

    const onClickSubmit = async () => {
        const updateLlmConfig: LlmConfig = {
            ...llmConfig,
            models,
        }

        dialog.open({
            variant: "danger",
            title: "LLM 연동 정보 수정",
            content: `LLM 연동 정보를 수정하려고 합니다. 계속 하시겠습니까?`,
            onConfirm: async () => {
                try {
                    await updateLlmProviderConfig(updateLlmConfig);
                    toast.success(`${llmConfig.provider} 연동 정보를 수정했습니다.`)
                } catch (e) {
                    toast.error(`${llmConfig.provider} 연동 정보를 수정하는 도중 에러가 발생했습니다.`)
                }
            }
        });
    }


    return (
        <div className="w-full h-full flex flex-col gap-10 border border-gray-400 rounded-lg p-4">
            <div className="flex justify-between items-center">
                <p className="font-bold text-xl">
                    {llmConfig.provider}
                </p>
                <button className="btn btn-info btn-sm"
                        onClick={onClickSubmit}>
                    {intl.formatMessage({id: "i0201"})}
                </button>
            </div>
            <ModelTable models={models} setModels={setModels}/>
        </div>
    )
}

type ModelTableProps = {
    models: string[];
    setModels: React.Dispatch<React.SetStateAction<string[]>>;
}

const ModelTable: React.FC<ModelTableProps> = (props) => {
    const {models, setModels} = props
    const [inputValue, setInputValue] = useState("");


    const onClickAdd = () => {
        const model = inputValue.trim();
        if (!model)
            return;

        if (models.includes(model)) {
            toast.error("이미 추가된 모델입니다.");
            return;
        }
        setModels(prev => [...prev, model]);
        setInputValue("");
    };


    const onClickDelete = (model: string) => {
        setModels(prev => prev.filter(m => m !== model));
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter")
            onClickAdd();
    };

    return (
        <div className="flex flex-col gap-3">
            <Label>모델 정보</Label>
            <div className="flex items-center gap-2">
                <Input className="flex-1"
                       placeholder="모델명 입력"
                       value={inputValue}
                       onChange={e => setInputValue(e.target.value)}
                       onKeyDown={onKeyDown}
                />
                <button className="btn btn-sm btn-outline" onClick={onClickAdd}>
                    추가
                </button>
            </div>
            {models.length === 0 && (
                <div className="text-xs">
                    등록된 모델이 없습니다.
                </div>
            )}
            {models.length > 0 && (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[50%] px-2">모델 명</TableHead>
                            <TableHead className="px-2">상태</TableHead>
                            <TableHead className="w-[10%] px-2"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {models.map((model, index) => (
                            <TableRow key={index} className="border-0">
                                <TableCell className="p-2">{model}</TableCell>
                                <TableCell className="p-2"></TableCell>
                                <TableCell className="p-2">
                                    <button className="btn btn-ghost btn-sm text-red-400 hover:text-red-600"
                                            onClick={() => onClickDelete(model)}
                                    >
                                        삭제
                                    </button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    )
}

export default LlmProviderSettingForm;